import { Injectable } from '@angular/core';
import { Firestore, doc, getDoc, setDoc, updateDoc, Timestamp, onSnapshot, Unsubscribe } from '@angular/fire/firestore';
import { NGXLogger } from 'ngx-logger';
import { getFirebaseConfigInfo } from '../../../../environments/firebase.config';
import { FirebaseStorageService } from './firebase-storage.service';
import { FascicoloProcessingData } from '../../models/firestore/fascicolo-processing.model';
import { environment } from '../../../../environments/environment';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class FirestoreConnectionService {
  private connectionTested = false;
  private connectionStatus: 'connected' | 'disconnected' | 'error' | 'unknown' = 'unknown';

  constructor(
    private firestore: Firestore,
    private firebaseStorage: FirebaseStorageService,
    private logger: NGXLogger
  ) {}

  /**
   * Testa la connessione a Firestore
   */
  async testConnection(): Promise<boolean> {
    if (this.connectionTested) {
      return this.connectionStatus === 'connected';
    }

    try {
      //this.logger.info('[FIRESTORE-CONNECTION] Inizio test connessione Firestore...');

      // Verifica la configurazione Firebase
      const configInfo = getFirebaseConfigInfo();
     /*  this.logger.info('[FIRESTORE-CONNECTION] Configurazione Firebase:', {
        isValid: configInfo.isValid,
        projectId: configInfo.projectId,
        environment: configInfo.environment
      }); */

      if (!configInfo.isValid) {
        this.logger.error('[FIRESTORE-DATABASE] Configurazione Firebase non valida');
        this.connectionStatus = 'error';
        this.connectionTested = true;
        return false;
      }

      // Test di connessione effettivo
      const testDocRef = doc(this.firestore, 'connection-test', 'test');

      // Prova a scrivere un documento di test
      await setDoc(testDocRef, {
        timestamp: Timestamp.now(),
        test: true,
        environment: configInfo.environment,
        projectId: configInfo.projectId
      });

      // Prova a leggere il documento di test
      const testDoc = await getDoc(testDocRef);

      if (testDoc.exists()) {
       /*  this.logger.info('[FIRESTORE-CONNECTION] ✅ Connessione Firestore riuscita!', {
          projectId: configInfo.projectId,
          environment: configInfo.environment,
          testData: testDoc.data()
        }); */

        this.connectionStatus = 'connected';
        this.connectionTested = true;
        return true;
      } else {
        this.logger.error('[FIRESTORE-CONNECTION] ❌ Test documento non trovato');
        this.connectionStatus = 'error';
        this.connectionTested = true;
        return false;
      }

    } catch (error) {
      this.logger.error('[FIRESTORE-CONNECTION] ❌ Errore connessione Firestore:', error);
      this.connectionStatus = 'error';
      this.connectionTested = true;
      return false;
    }
  }

  /**
   * Ottiene lo stato della connessione
   */
  getConnectionStatus(): 'connected' | 'disconnected' | 'error' | 'unknown' {
    return this.connectionStatus;
  }

  /**
   * Verifica se la connessione è stata testata
   */
  isConnectionTested(): boolean {
    return this.connectionTested;
  }

  /**
   * Resetta lo stato della connessione (per nuovi test)
   */
  resetConnectionStatus(): void {
    this.connectionTested = false;
    this.connectionStatus = 'unknown';
  }

  /**
   * Testa sia Firestore che Storage
   */
  async testAllConnections(): Promise<{
    firestore: { connected: boolean; error?: string; responseTime?: number };
    storage: { connected: boolean; error?: string; responseTime?: number };
    overall: { connected: boolean; services: string[] };
  }> {
    //this.logger.info('[FIREBASE-CONNECTION] Inizio test completo connessioni Firebase...');

    const startTime = Date.now();

    // Test Firestore
    const firestoreResult = await this.testConnection().then(connected => ({
      connected,
      responseTime: Date.now() - startTime
    })).catch(error => ({
      connected: false,
      error: error.message,
      responseTime: Date.now() - startTime
    }));

    // Test Storage (versione semplificata per evitare errori di injection context)
    const storageResult = await this.firebaseStorage.testStorageConnection();

    // Calcola risultato complessivo
    const connectedServices = [];
    if (firestoreResult.connected) connectedServices.push('Firestore');
    if (storageResult.connected) connectedServices.push('Storage');

    const overallResult = {
      connected: connectedServices.length > 0,
      services: connectedServices
    };

   /*  this.logger.info('[FIREBASE-CONNECTION] Test completo completato:', {
      firestore: firestoreResult,
      storage: storageResult,
      overall: overallResult
    });
 */
    return {
      firestore: firestoreResult,
      storage: storageResult,
      overall: overallResult
    };
  }

  /**
   * Ottiene informazioni dettagliate sulla connessione
   */
  getConnectionInfo(): {
    status: 'connected' | 'disconnected' | 'error' | 'unknown';
    tested: boolean;
    config: { isValid: boolean; projectId: string; environment: string };
  } {
    const configInfo = getFirebaseConfigInfo();

    return {
      status: this.connectionStatus,
      tested: this.connectionTested,
      config: configInfo
    };
  }

  /**
   * Inserisce un documento nella collection fascicoli-processing
   * @param fascicoloProcessingData I dati del fascicolo da salvare
   * @returns Promise<boolean> True se il salvataggio è riuscito, false altrimenti
   */
  async insertFascicoloProcessing(fascicoloProcessingData: FascicoloProcessingData): Promise<boolean> {
    try {
      // Verifica che l'UID sia presente
      if (!fascicoloProcessingData.fascicoloProcessingUID) {
        this.logger.error('[FIRESTORE-FASCICOLI-PROCESSING] UID del fascicolo non presente');
        return false;
      }

      // Prepara i dati per il salvataggio, convertendo le date in Timestamp
      const dataToSave = {
        ...fascicoloProcessingData,
        dataCreazione: fascicoloProcessingData.dataCreazione ? Timestamp.fromDate(fascicoloProcessingData.dataCreazione) : Timestamp.now(),
      //  dataAggiornamento: Timestamp.now(),
     //   dataCompletamento: fascicoloProcessingData.dataCompletamento ? Timestamp.fromDate(fascicoloProcessingData.dataCompletamento) : null,
        domandaSostegno: {
          ...fascicoloProcessingData.domandaSostegno,
          uploadedAt: Timestamp.fromDate(fascicoloProcessingData.domandaSostegno.uploadedAt)
        },
        domandePagamento: fascicoloProcessingData.domandePagamento?.map(dp => ({
          ...dp,
          uploadedAt: Timestamp.fromDate(dp.uploadedAt)
        })) || []
      };

      // Crea il riferimento al documento usando l'UID come ID
      const docRef = doc(this.firestore, environment.firebase.collection_fascicoli_processing, fascicoloProcessingData.fascicoloProcessingUID);

      // Salva il documento
      await setDoc(docRef, dataToSave);

      this.logger.info('[FIRESTORE-FASCICOLI-PROCESSING] ✅ Fascicolo salvato con successo', {
        uid: fascicoloProcessingData.fascicoloProcessingUID,
        stato: fascicoloProcessingData.statoProcessing
      });

      return true;

    } catch (error) {
      this.logger.error('[FIRESTORE-FASCICOLI-PROCESSING] ❌ Errore nel salvataggio del fascicolo:', error);
      return false;
    }
  }

  /**
   * Si mette in ascolto sui cambiamenti di un documento fascicolo-processing specifico
   * @param fascicoloProcessingUID L'UID del fascicolo da monitorare
   * @param callback Funzione chiamata ogni volta che il documento cambia
   * @returns Unsubscribe function per fermare l'ascolto
   */
  listenToFascicoloProcessingChanges(
    fascicoloProcessingUID: string,
    callback: (data: FascicoloProcessingData | null) => void
  ): Unsubscribe {
    const docRef = doc(this.firestore, environment.firebase.collection_fascicoli_processing, fascicoloProcessingUID);

    this.logger.info('[FIRESTORE-FASCICOLI-PROCESSING] Inizio ascolto cambiamenti fascicolo', {
      uid: fascicoloProcessingUID
    });

    return onSnapshot(docRef,
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          const data = docSnapshot.data() as FascicoloProcessingData;

          // Converte i Timestamp di Firestore in Date JavaScript
          const processedData: FascicoloProcessingData = {
            ...data,
            dataCreazione: data.dataCreazione ? (data.dataCreazione as any).toDate?.() || data.dataCreazione : undefined,
            dataAggiornamento: data.dataAggiornamento ? (data.dataAggiornamento as any).toDate?.() || data.dataAggiornamento : undefined,
            dataCompletamento: data.dataCompletamento ? (data.dataCompletamento as any).toDate?.() || data.dataCompletamento : undefined,
            domandaSostegno: data.domandaSostegno ? {
              ...data.domandaSostegno,
              uploadedAt: (data.domandaSostegno.uploadedAt as any).toDate?.() || data.domandaSostegno.uploadedAt
            } : data.domandaSostegno,
            domandePagamento: data.domandePagamento?.map(dp => ({
              ...dp,
              uploadedAt: (dp.uploadedAt as any).toDate?.() || dp.uploadedAt
            })) || []
          };

          this.logger.info('[FIRESTORE-FASCICOLI-PROCESSING] Documento aggiornato', {
            uid: fascicoloProcessingUID,
            stato: processedData.statoProcessing
          });

          callback(processedData);
        } else {
          this.logger.warn('[FIRESTORE-FASCICOLI-PROCESSING] Documento non trovato', {
            uid: fascicoloProcessingUID
          });
          callback(null);
        }
      },
      (error) => {
        this.logger.error('[FIRESTORE-FASCICOLI-PROCESSING] Errore nell\'ascolto cambiamenti:', error);
        callback(null);
      }
    );
  }

  /**
   * Versione Observable per ascoltare i cambiamenti del fascicolo
   * @param fascicoloProcessingUID L'UID del fascicolo da monitorare
   * @returns Observable che emette i dati del fascicolo quando cambiano
   */
  getFascicoloProcessingChanges(fascicoloProcessingUID: string): Observable<FascicoloProcessingData | null> {
    return new Observable(observer => {
      const unsubscribe = this.listenToFascicoloProcessingChanges(
        fascicoloProcessingUID,
        (data) => observer.next(data)
      );

      // Cleanup function
      return () => {
        unsubscribe();
        this.logger.info('[FIRESTORE-FASCICOLI-PROCESSING] Ascolto interrotto', {
          uid: fascicoloProcessingUID
        });
      };
    });
  }

  /**
   * Aggiorna un campo specifico di un documento fascicolo-processing
   * @param fascicoloProcessingUID L'UID del fascicolo da aggiornare
   * @param field Il nome del campo da aggiornare
   * @param value Il nuovo valore del campo
   * @returns Promise<boolean> True se l'aggiornamento è riuscito, false altrimenti
   */
  async updateFascicoloProcessingField(
    fascicoloProcessingUID: string,
    field: string,
    value: any
  ): Promise<boolean> {
    try {
      // Verifica che l'UID sia presente
      if (!fascicoloProcessingUID) {
        this.logger.error('[FIRESTORE-FASCICOLI-PROCESSING] UID del fascicolo non presente');
        return false;
      }

      // Prepara i dati per l'aggiornamento
      const updateData: any = {};
      updateData[field] = value;

      // Se si sta aggiornando lo stato, aggiorna anche dataAggiornamento
      if (field === 'statoProcessing') {
        updateData.dataAggiornamento = Timestamp.now();
      }

      // Se si sta aggiornando una data, convertila in Timestamp
      if (field.includes('data') && value instanceof Date) {
        updateData[field] = Timestamp.fromDate(value);
      }

      // Crea il riferimento al documento
      const docRef = doc(this.firestore, environment.firebase.collection_fascicoli_processing, fascicoloProcessingUID);

      // Aggiorna il documento
      await updateDoc(docRef, updateData);

      this.logger.info('[FIRESTORE-FASCICOLI-PROCESSING] ✅ Campo aggiornato con successo', {
        uid: fascicoloProcessingUID,
        field: field,
        value: value
      });

      return true;

    } catch (error) {
      this.logger.error('[FIRESTORE-FASCICOLI-PROCESSING] ❌ Errore nell\'aggiornamento del campo:', error);
      return false;
    }
  }

  /**
   * Aggiorna più campi di un documento fascicolo-processing
   * @param fascicoloProcessingUID L'UID del fascicolo da aggiornare
   * @param updates Oggetto con i campi e valori da aggiornare
   * @returns Promise<boolean> True se l'aggiornamento è riuscito, false altrimenti
   */
  async updateFascicoloProcessingFields(
    fascicoloProcessingUID: string,
    updates: Record<string, any>
  ): Promise<boolean> {
    try {
      // Verifica che l'UID sia presente
      if (!fascicoloProcessingUID) {
        this.logger.error('[FIRESTORE-FASCICOLI-PROCESSING] UID del fascicolo non presente');
        return false;
      }

      // Prepara i dati per l'aggiornamento
      const updateData: any = { ...updates };

      // Aggiungi sempre dataAggiornamento
      updateData.dataAggiornamento = Timestamp.now();

      // Converti le date in Timestamp
      Object.keys(updateData).forEach(key => {
        if (key.includes('data') && updateData[key] instanceof Date) {
          updateData[key] = Timestamp.fromDate(updateData[key]);
        }
      });

      // Crea il riferimento al documento
      const docRef = doc(this.firestore, environment.firebase.collection_fascicoli_processing, fascicoloProcessingUID);

      // Aggiorna il documento
      await updateDoc(docRef, updateData);

      this.logger.info('[FIRESTORE-FASCICOLI-PROCESSING] ✅ Campi aggiornati con successo', {
        uid: fascicoloProcessingUID,
        updates: updates
      });

      return true;

    } catch (error) {
      this.logger.error('[FIRESTORE-FASCICOLI-PROCESSING] ❌ Errore nell\'aggiornamento dei campi:', error);
      return false;
    }
  }
}
